import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { ESearchFirearmsFormLabelsTypes } from 'constants/SearchFormLabelsTypes';
import { initialSearchFirearmsFormValues, ISearchFirearmsFormValues } from 'models/form';
import { prepareFilters } from 'helpers/filters';
import { FormAutocomplete } from 'components/molecules/FormAutocomplete';
import { useTranslations } from 'hooks/useTranslations';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { filterOutAncillaryStates } from 'pages/LegalEntitiesPage/FirearmSearchForm';

interface IFirearmSearchForm {
  onSubmit: (arg: ISearchFirearmsFormValues) => void;
}

const SearchFirearmsForm = ({ onSubmit }: IFirearmSearchForm) => {
  const { make, model, state, caliber, legality, manufacturer, loadDictionaries } =
    useLegalEntities();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.Legality,
        EDictionaryTypes.Manufacturer,
        EDictionaryTypes.Make,
        EDictionaryTypes.Model,
        EDictionaryTypes.State,
        EDictionaryTypes.Caliber,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchFirearmsFormValues>({
    defaultValues: initialSearchFirearmsFormValues,
  });
  const [values, setValues] = useState<ISearchFirearmsFormValues>(
    initialSearchFirearmsFormValues,
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchFirearmsFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialSearchFirearmsFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <SearchWrapper>
      <Grid container spacing={2} columns={10} sx={{ justifyContent: 'flex-end' }}>
        <Grid size={{ xs: 10, md: 5, lg: 4 }} sx={{ mt: 1 }}>
          <Button
            variant={EButtonVariants.outlined}
            size={EButtonSizes.small}
            id="search-advanced_filters-button"
            label={t('searchForLegalEntitysFirearm.advancedFilters.button')}
            onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
            fullWidth
            sx={{ mb: advancedFiltersOpen ? 3 : 0 }}
          />
        </Grid>
      </Grid>
      <form onSubmit={handleSubmit(onSubmit)}>
        {advancedFiltersOpen && (
          <Grid container spacing={2} columns={10}>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="serialNumber"
                label={t('searchForLegalEntitysFirearm.serialNumber.label')}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="marking"
                label={t('searchForLegalEntitysFirearm.markings.label')}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(filterOutAncillaryStates(state))}
                name="stateUniqueId"
                setValue={setValue}
                canBeEmpty
                label={t('searchForLegalEntitysFirearm.state.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(legality)}
                name="legalityUniqueId"
                setValue={setValue}
                canBeEmpty
                label={t('searchForLegalEntitysFirearm.legality.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormAutocomplete
                name="makeUniqueId"
                options={mapDictionaryToOptionProp(make)}
                label={t('searchForLegalEntitysFirearm.make.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormAutocomplete
                name="modelUniqueId"
                options={mapDictionaryToOptionProp(model)}
                label={t('searchForLegalEntitysFirearm.model.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormAutocomplete
                name="manufacturerUniqueId"
                options={mapDictionaryToOptionProp(manufacturer)}
                label={t('searchForLegalEntitysFirearm.manufacturer.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormAutocomplete
                name="caliberUniqueId"
                options={mapDictionaryToOptionProp(caliber)}
                label={t('searchForLegalEntitysFirearm.calibre.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }} />
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                id="search-firearm-button"
                label={t('searchForLegalEntitysFirearm.search.button')}
                type="submit"
                fullWidth
                alignWithInputSX={{ xs: 10, sm: 5, md: 5, lg: 2, columns: 10 }}
              />
            </Grid>
          </Grid>
        )}
      </form>
      <SelectedFilters
        name="firearm"
        values={values}
        labels={ESearchFirearmsFormLabelsTypes}
        handleDelete={handleResetField}
        noPreset
        dictionaries={{
          makeUniqueId: prepareFilters(make, 'uniqueId', 'name'),
          modelUniqueId: prepareFilters(model, 'uniqueId', 'name'),
          manufacturerUniqueId: prepareFilters(manufacturer, 'uniqueId', 'name'),
          caliberUniqueId: prepareFilters(caliber, 'uniqueId', 'name'),
          stateUniqueId: prepareFilters(state, 'uniqueId', 'name'),
          legalityUniqueId: prepareFilters(legality, 'uniqueId', 'name'),
        }}
        showSaveFilters={false}
        saveRouterStateAfterClearAll={true}
        saveQueryParams={['type', 'groupId']}
      />
    </SearchWrapper>
  );
};

export { SearchFirearmsForm };
